import Color from 'color';
import loMemo from 'lodash/memoize.js';
// need to leave this imported even though it doesn't look like it's being used for proper typing of `lodash/memoize`

export type ColorRgbInfo = {
  /** From 0 - 255 */
  r: number;
  /** From 0 - 255 */
  g: number;
  /** From 0 - 255 */
  b: number;
  /** From 0 - 1 */
  alpha?: number;
};

export type ColorHslInfo = {
  /** From 0 - 255 */
  h: number;
  /** From 0 - 255 */
  s: number;
  /** From 0 - 255 */
  l: number;
  /** From 0 - 1 */
  alpha?: number;
};

export type ColorFormat = 'hex' | 'hsl' | 'rgb';

/**
 * If a number is less than 1, round it to a hundredth (i.e. 0.87), else round to nearest integer.
 */
function roundNumber(value: number): number {
  return value < 1 ? Number(value.toFixed(2)) : Math.round(value);
}

function roundObjectValuesNumber<Obj extends Record<string, number>>(
  obj: Obj,
): Record<keyof Obj, number> {
  return Object.entries(obj).reduce(
    (cur, [key, value]) => {
      const k = key as keyof Obj;
      cur[k] = roundNumber(value);
      return cur;
    },
    {} as Record<keyof Obj, number>,
  );
}

/**
 * Convert a transparency to the 7th & 8th character for an 8 digit hex color
 * @link https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4
 */
function colorAlphaToHex(value: number): string {
  let val = Math.max(0, value);
  val = Math.min(val, 255);
  val = Math.ceil(val);
  return (
    '0123456789ABCDEF'.charAt((val - (val % 16)) / 16) +
    '0123456789ABCDEF'.charAt(val % 16)
  );
}

function getColorFormat(color: string): ColorFormat | null {
  if (color.startsWith('#')) return 'hex';
  if (color.startsWith('rgb')) return 'rgb';
  if (color.startsWith('hsl')) return 'hsl';
  return null;
}

function analyzeColorBase(color: string) {
  try {
    const c = Color(color);
    const rgb = c.rgb();
    const rgbInfo = roundObjectValuesNumber(
      rgb.object() as unknown as ColorRgbInfo,
    );
    const isTransparent =
      typeof rgbInfo.alpha === 'number' && rgbInfo.alpha !== 1;
    const rgbString = isTransparent
      ? `rgba(${rgbInfo.r}, ${rgbInfo.g}, ${rgbInfo.b}, ${rgbInfo.alpha})`
      : `rgb(${rgbInfo.r}, ${rgbInfo.g}, ${rgbInfo.b})`;
    const hsl = c.hsl();
    const hslInfo = roundObjectValuesNumber(
      hsl.object() as unknown as ColorHslInfo,
    );
    const hslString = isTransparent
      ? `hsla(${hslInfo.h}, ${hslInfo.s}%, ${hslInfo.l}%, ${hslInfo.alpha})`
      : `hsl(${hslInfo.h}, ${hslInfo.s}%, ${hslInfo.l}%)`;
    let hex = c.hex();
    if (isTransparent) {
      // 6 => 8 character transparent hex
      hex = `${hex}${colorAlphaToHex(rgbInfo.alpha * 256)}`;
    }
    return {
      original: color,
      format: getColorFormat(color),
      rgbInfo,
      rgbString,
      hslInfo,
      hslString,
      hex,
      isTransparent,
      luminosity: c.luminosity(),
      isDark: c.isDark(),
      isLight: c.isLight(),
      color: c,
    };
  } catch (e) {
    throw new Error(
      `Could not analyze the color "${color}" - ${
        e instanceof Error ? e.message : ''
      }`,
    );
  }
}

export const analyzeColor = loMemo(analyzeColorBase);

/**
 * Is a valid css color
 */
export function isColor(color: string): boolean {
  try {
    analyzeColor(color);
    return true;
  } catch (e) {
    return false;
  }
}

export function assertColor(color: string): asserts color is string {
  analyzeColor(color);
}

/**
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 */
export function getColorRgbInfo(color: string): ColorRgbInfo {
  return analyzeColor(color).rgbInfo;
}

/**
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 */
export function getColorHslInfo(color: string): ColorHslInfo {
  return analyzeColor(color).hslInfo;
}

/**
 * Has Opacity
 * Returns true if a color has opacity
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 */
export function hasOpacity(color: string): boolean {
  return !analyzeColor(color).isTransparent;
}

/**
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 */
export function getColorLuminosity(color: string): number {
  return analyzeColor(color).luminosity;
}

/**
 * Color Contrast
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 * @param color - color value to check. supports any number of formats (hex, rgb, hsl, etc)
 * @returns {boolean} - true if the color is "dark" (ie. you should use a lighter color on top) and false if the color is light.
 */
export function isDarkColor(color: string): boolean {
  return analyzeColor(color).isDark;
}

/**
 * Color Contrast
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 * @param color - color value to check. supports any number of formats (hex, rgb, hsl, etc)
 * @returns {boolean} - true if the color is "light" (ie. you should use a darker color on top) and false if the color is dark.
 */
export function isLightColor(color: string): boolean {
  return analyzeColor(color).isLight;
}

/**
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 */
export function convertColor({
  color,
  format,
}: {
  color: string;
  format: ColorFormat;
}): string {
  assertColor(color);
  switch (format) {
    case 'rgb': {
      const rgb = getColorRgbInfo(color);
      return Color(rgb).string();
    }
    case 'hsl': {
      const hsl = getColorHslInfo(color);
      return Color(hsl).string();
    }
    case 'hex':
    default:
      return Color(color).hex();
  }
}

/**
 * @deprecated - use `analyzeColor`
 * @see analyzeColor
 */
export function convertTransparentColorToHex(color: string): string {
  return analyzeColor(color).hex;
}

/**
 * Contrast checking.
 *
 * IMPORTANT: CANNOT USE ALPHA CHANNELS IN CONTRAST CALCULATIONS.
 * This can only compare 2 SOLID colors.
 */
export function isColorContrastEnough({
  foregroundColor,
  bgColor,
}: {
  foregroundColor: string;
  bgColor: string;
}): boolean {
  const f = analyzeColor(foregroundColor);
  const b = analyzeColor(bgColor);

  // https://www.w3.org/TR/WCAG20/#contrast-ratiodef
  // (L1 + 0.05) / (L2 + 0.05), where
  // L1 is the relative luminance of the lighter of the colors, and
  // L2 is the relative luminance of the darker of the colors.
  const contrast = f.color.contrast(b.color);

  // https://webaim.org/resources/contrastchecker/
  return contrast >= 4.5;
}
